// Function to map backend status strings to frontend status values
const mapChangeStatus = status => {
    if (!status) return null
    if (status === 'ReadyForReview') return 'pending'
    if (status === 'Finalizing') return 'finalized'
    return status // Fallback for any other status values
}

// Function to normalize a pattern/category
const normalizePattern = pattern => ({
    id: pattern.id,
    colorId: pattern.color_id,
    type: pattern.type,
    name: pattern.name,
    description: pattern.description,
    comparisonDescription: pattern.comparison_description,
    descriptionChangeStatus: mapChangeStatus(pattern.description_change_status),
    feedback: pattern.feedback,
    grade: pattern.grade,
    attributes: pattern.attributes,
    conceptualLabels: pattern.conceptual_labels,
    philosophyText: pattern.philosophy_text,
    order: pattern.order,
    autoApprove: pattern.auto_approve !== undefined ? pattern.auto_approve : true,
})

// Function to normalize a QuestionRubric
const normalizeQuestionRubric = rubric => ({
    id: rubric.id,
    rubricId: rubric.rubric_id,
    name: rubric.name,
    position: rubric.position,
    processing: {
        status: rubric.processing_status,
        message: rubric.processing_msg,
        progress: rubric.processing_progress,
    },
    llmFeatureQuestions: rubric.llm_feature_questions,
    useLlm: rubric.use_llm,
    llmWithReasoning: rubric.llm_with_reasoning,
    trainingMode: rubric.training_mode,
    thresholds: {
        low: rubric.low_threshold,
        high: rubric.high_threshold,
        veryHigh: rubric.very_high_threshold,
    },
    approveDiagram: rubric.approve_diagram,
    lastTrained: rubric.last_trained,
    categories: (rubric.patterns || []).map(normalizePattern),
})

// Function to normalize a single question
const normalizeQuestion = question => {
    const questionRubrics = (question.rubrics || []).map(normalizeQuestionRubric)

    const normalized = {
        id: question.id,
        name: question.name,
        text: question.text,
        comparisonGradingReference: question.comparison_grading_reference_content,
        gradingReferenceChangeStatus: mapChangeStatus(question.grading_reference_change_status),
        order: question.order,
        sectionId: question.section,
        questionType: question.question_type, // standard, context_only, global, input_only
        questionRubrics,
        wordsLimit: question.word_limit,
        dependsOnQuestionIds: question.depends_on,
        parentId: question.parent_id,
        createdAt: question.created_at,
        requireCot: question.require_cot,
        note: question.note,
    }
    
    return normalized
}


// Main function
export const normalizeFetchQuestionsResponse = response => response.map(normalizeQuestion)

export const normalizeFetchQuestionMetadataResponse = response => {
    const normalizedHighlights = {}
    for (const [answerId, categories] of Object.entries(response.highlights)) {
        let categoryHighlights = {}
        for (const [categoryId, highlights] of Object.entries(categories)) {
            categoryHighlights[categoryId] = highlights.masked_ranges.map((range, index) => ({
                start: range[0],
                end: range[1],
                importance: highlights.relative_importances[index],
            }))
        }
        normalizedHighlights[answerId] = categoryHighlights
    }

    return {
        highlights: normalizedHighlights,
    }
}

export const normalizeFetchQuestionChangesHistoryResponse = response =>
    response
        .map(change => ({
            field: change.updated_field,
            oldValue: change.old_value,
            newValue: change.new_value,
            createdAt: change.created_at,
            actor: change.actor,
            isRubricRefinement: change.is_rubric_refinement,
        }))
